import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { kbApi } from '@carrent/api'

/**
 * Данные базы знаний.
 *
 * Загрузка и состояния живут здесь: консоль и приложение водителя
 * читают одни и те же статьи, различается только композиция —
 * три колонки против списка с bottom sheet.
 */

const msg = (e, fallback) => e?.response?.data?.message || fallback

export function useKbSpaces() {
  const [spaces, setSpaces] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    kbApi.spaces()
      .then((r) => setSpaces(r.data?.data || []))
      .catch((e) => {
        setError(msg(e, 'Не удалось загрузить пространства'))
        setSpaces([])
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  return { spaces, loading, error, reload: load }
}

export function useKbTree(spaceKey) {
  const [space, setSpace] = useState(null)
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(Boolean(spaceKey))
  const [error, setError] = useState('')

  const load = useCallback(() => {
    if (!spaceKey) {
      setSpace(null)
      setPages([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    kbApi.tree(spaceKey)
      .then((r) => {
        setSpace(r.data?.data?.space || null)
        setPages(r.data?.data?.pages || [])
      })
      .catch((e) => {
        setError(msg(e, 'Не удалось загрузить дерево страниц'))
        setPages([])
      })
      .finally(() => setLoading(false))
  }, [spaceKey])

  useEffect(() => { load() }, [load])

  const tree = useMemo(() => buildTree(pages), [pages])
  return { space, pages, tree, loading, error, reload: load }
}

export function useKbPage(pageId) {
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(Boolean(pageId))
  const [error, setError] = useState('')

  const load = useCallback(() => {
    if (!pageId) {
      setPage(null)
      setLoading(false)
      setError('')
      return
    }
    setLoading(true)
    setError('')
    kbApi.page(pageId)
      .then((r) => setPage(r.data?.data || null))
      .catch((e) => {
        setPage(null)
        setError(e?.response?.status === 404
          ? 'Статья не найдена'
          : msg(e, 'Не удалось загрузить статью'))
      })
      .finally(() => setLoading(false))
  }, [pageId])

  useEffect(() => { load() }, [load])

  return { page, loading, error, reload: load }
}

/**
 * Поиск с задержкой.
 *
 * Запрос уходит не на каждое нажатие: без задержки строка «регламент»
 * даёт девять запросов, из которых нужен последний. Ответы на устаревшие
 * запросы отбрасываются по счётчику — иначе медленный ранний ответ
 * перезапишет свежий.
 */
export function useKbSearch(query, { delay = 300, minLength = 2 } = {}) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const seq = useRef(0)

  useEffect(() => {
    const q = (query || '').trim()
    if (q.length < minLength) {
      setResults([])
      setLoading(false)
      setError('')
      return undefined
    }

    const current = seq.current + 1
    seq.current = current
    setLoading(true)

    const timer = setTimeout(() => {
      kbApi.search(q)
        .then((r) => {
          if (seq.current !== current) return
          setResults(r.data?.data || [])
          setError('')
        })
        .catch((e) => {
          if (seq.current !== current) return
          setResults([])
          setError(msg(e, 'Поиск недоступен'))
        })
        .finally(() => {
          if (seq.current === current) setLoading(false)
        })
    }, delay)

    return () => clearTimeout(timer)
  }, [query, delay, minLength])

  return { results, loading, error }
}

/** Плоский список страниц → дерево по parent_id, с сохранением порядка. */
export function buildTree(pages) {
  const byId = new Map()
  pages.forEach((p) => byId.set(p.id, { ...p, children: [] }))

  const roots = []
  byId.forEach((node) => {
    const parent = node.parent_id != null ? byId.get(node.parent_id) : null
    // Потерянный родитель (нет прав, архив) — показываем в корне,
    // иначе страница исчезнет из дерева целиком.
    if (parent) parent.children.push(node)
    else roots.push(node)
  })

  const sort = (list) => {
    list.sort((a, b) => (a.position - b.position)
      || a.title.localeCompare(b.title, 'ru'))
    list.forEach((n) => sort(n.children))
  }
  sort(roots)
  return roots
}

/** Путь от корня до страницы — для хлебных крошек. */
export function pathTo(pages, pageId) {
  const byId = new Map(pages.map((p) => [p.id, p]))
  const path = []
  let cur = byId.get(pageId)
  const seen = new Set()
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id)
    path.unshift(cur)
    cur = cur.parent_id != null ? byId.get(cur.parent_id) : null
  }
  return path
}
